import type { JSX } from '@builder.io/qwik';
import {
  useContextProvider,
  component$,
  Slot,
  useContext,
} from '@builder.io/qwik';
import type { GalleryColumnBlockInterface } from '~/types/AppData/PageBuilder/GalleryColumnBlockInterface';
import { cva } from 'class-variance-authority';
import { CardContext } from '~/contexts/CardContext';
import type { ArticleInterface } from '~/types/AppData/PageContent/ArticleInterface';
import AnalyticsContainer from '~/components/Shared/Analytics/AnalyticsContainer';
import { getOneIndexing } from '~/utilities/getOneIndexing';
import type { SectionHeaderBlockInterface } from '~/types/AppData/PageBuilder/SectionHeaderBlockInterface';

const cssClasses = cva('gallery-column group w-full md:order-none relative', {
  variants: {
    width: {
      threeColLarge: 'md:w-[50.6%]',
      threeColSmall: 'md:w-[24.7%]',
      twoColLarge: 'md:w-[56%]',
      twoColSmall: 'md:w-[44%]',
      twoColHalf: 'md:w-1/2',
      full: 'w-full',
    },
    mobileOrder: {
      1: 'order-1',
      2: 'order-2',
      3: 'order-3',
      4: 'order-4',
    },
    mobileDirection: {
      horizontal: 'grid grid-cols-2 md:flex md:flex-col',
      vertical: 'flex flex-col',
    },
    showImage: {
      false: '!gap-0',
    },
    cardsDivider: {
      true: '[&>.card]:border-b [&_.card]:border-borderGrey [&>.card:last-of-type]:border-none [&>.card]:py-[5px] [&>.card]:md:py-2.5 [&>.card:first-of-type]:pt-0 [&>.card:last-of-type]:pb-0',
      false: 'gap-4',
    },
    showDescription: {
      true: 'gap-4 md:gap-6',
      false: '',
    },
  },
});

export default component$<
  GalleryColumnBlockInterface['props'] & {
    content: ArticleInterface[];
    index: number;
    items: GalleryColumnBlockInterface['children'];
  }
>(
  ({
    rows,
    card,
    width,
    mobileOrder,
    mobileDirection,
    items,
    index,
  }): JSX.Element => {
    const numCards = items?.filter((each) => each.type === 'Card').length ?? 0;
    const numSectionHeaders =
      items?.filter(
        (each) =>
          each.type === 'SectionHeader' &&
          'props' in each &&
          (each as SectionHeaderBlockInterface).props.link?.text
      ).length ?? 0;
    const numLinks = numCards + numSectionHeaders;
    const prev = useContext(CardContext);

    useContextProvider(CardContext, {
      ...prev,
      ...card,
      rows: numCards ? numCards : rows,
      columnWidth: width,
    });

    return (
      <div
        class={cssClasses({
          width,
          mobileOrder,
          mobileDirection: mobileDirection ?? 'vertical',
          cardsDivider: card.divider ?? false,
          showDescription: card.showDescription,
          showImage: card.showImage,
        })}
      >
        <AnalyticsContainer
          component_name={`GalleryColumn-${mobileOrder}`}
          column_number_of_links={numLinks}
          component_type='GalleryColumn'
          component_placement_desktop={getOneIndexing(index)}
          // mobileOrder must be set accurately for every GalleryColumn
          component_placement={mobileOrder}
        >
          <Slot />
        </AnalyticsContainer>
      </div>
    );
  }
);
